// @ts-nocheck
/**
 * Cache Management Interface Component
 * Carbon Design System interface for cache management and performance monitoring
 */

import React, { useState, useEffect } from 'react';
import './CacheManagementInterface.css';
import {
  Grid,
  Column,
  Tile,
  Button,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  ProgressIndicator,
  ProgressStep,
  InlineNotification,
  TextInput,
  Modal,
  Loading,
  Heading,
} from '@carbon/react';
import {
  Renew,
  TrashCan,
  Analytics,
  Warning,
  Checkmark,
  CheckmarkFilled,
  WarningFilled,
  ErrorFilled,
  Time,
} from '@carbon/icons-react';

import type {
  CacheManagementState,
  CacheMetrics,
  CacheInvalidationEvent,
} from '../../../shared/interfaces/ContentManagementAdmin';

interface CacheManagementInterfaceProps {
  onCacheInvalidate: (contentIds: string[]) => void;
  onPerformanceAlert: (alert: any) => void;
}

const CacheManagementInterface: React.FC<CacheManagementInterfaceProps> = ({
  onCacheInvalidate,
  onPerformanceAlert,
}) => {
  const [state, setState] = useState<CacheManagementState>({
    cacheMetrics: {
      hitRate: 0,
      missRate: 0,
      totalRequests: 0,
      cacheSize: 0,
      oldestEntry: '',
      newestEntry: '',
      avgResponseTime: 0,
    },
    invalidationHistory: [],
    performanceData: {
      hourlyStats: [],
      regionPerformance: [],
      contentTypePerformance: [],
    },
    loading: true,
    error: null,
  });

  const [invalidateModalOpen, setInvalidateModalOpen] = useState(false);
  const [contentIdsToInvalidate, setContentIdsToInvalidate] = useState('');

  useEffect(() => {
    loadCacheMetrics();
    const interval = setInterval(loadCacheMetrics, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadCacheMetrics = async () => {
    setState(prev => ({ ...prev, loading: true }));

    try {
      const [metricsResponse, historyResponse, performanceResponse] = await Promise.all([
        fetch('/api/v1/content/stats'),
        fetch('/api/v1/content/invalidation-history'),
        fetch('/api/v1/content/performance'),
      ]);

      const metrics = metricsResponse.ok ? await metricsResponse.json() : {};
      const history = historyResponse.ok ? await historyResponse.json() : { data: [] };
      const performance = performanceResponse.ok ? await performanceResponse.json() : { data: {} };

      // Map the stats response to the expected cacheMetrics structure
      const cacheMetrics = {
        hitRate: metrics.cacheHitRate || 0,
        missRate: metrics.cacheHitRate ? 1 - metrics.cacheHitRate : 1,
        totalRequests: metrics.totalKeys || 0,
        cacheSize: metrics.totalKeys || 0,
        oldestEntry: '',
        newestEntry: '',
        avgResponseTime: metrics.avgLatency || 0,
      };

      setState(prev => ({
        ...prev,
        cacheMetrics,
        invalidationHistory: history.data || [],
        performanceData: performance.data || prev.performanceData,
        loading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load cache metrics',
        loading: false,
      }));
    }
  };

  const handleBulkInvalidate = async () => {
    const contentIds = contentIdsToInvalidate
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);

    if (contentIds.length === 0) return;

    try {
      const response = await fetch('/api/v1/content/cache/invalidate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentIds }),
      });

      if (!response.ok) {
        throw new Error('Failed to invalidate cache');
      }

      onCacheInvalidate(contentIds);
      setInvalidateModalOpen(false);
      setContentIdsToInvalidate('');
      loadCacheMetrics(); // Refresh metrics
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Cache invalidation failed',
      }));
    }
  };

  const renderMetricsTile = (
    title: string,
    value: string | number,
    subtitle?: string,
    status?: 'good' | 'warning' | 'error'
  ) => (
    <Tile
      role="region"
      aria-label={`${title} metric: ${value}${subtitle ? ` (${subtitle})` : ''}`}
      className={`
        cache-metric-tile
        ${status === 'error' ? 'cache-metric-tile--error' : ''}
        ${status === 'warning' ? 'cache-metric-tile--warning' : ''}
      `}
    >
      <div className="cache-metric-value">
        {status === 'error' && <ErrorFilled size={16} />}
        {status === 'warning' && <WarningFilled size={16} />}
        {status === 'good' && <CheckmarkFilled size={16} />}
        <span>{value}</span>
      </div>
      <Heading size="sm" className="cache-metric-title">
        {title}
      </Heading>
      {subtitle && <p className="cache-metric-subtitle">{subtitle}</p>}
    </Tile>
  );

  const invalidationHeaders = [
    { key: 'timestamp', header: 'Time' },
    { key: 'contentIds', header: 'Content IDs' },
    { key: 'triggeredBy', header: 'Triggered By' },
    { key: 'status', header: 'Status' },
    { key: 'duration', header: 'Duration' },
  ];

  const invalidationRows = state.invalidationHistory.map(event => ({
    id: event.id,
    timestamp: new Date(event.timestamp).toLocaleString(),
    contentIds: event.contentIds.length > 1 ? `${event.contentIds.length} items` : event.contentIds[0] || 'N/A',
    triggeredBy: event.triggeredBy,
    status: (
      <Tag
        type={event.status === 'completed' ? 'green' : event.status === 'failed' ? 'red' : 'blue'}
        renderIcon={event.status === 'completed' ? CheckmarkFilled : event.status === 'failed' ? ErrorFilled : Time}
      >
        {event.status}
      </Tag>
    ),
    duration: event.duration ? `${event.duration}ms` : '-',
  }));

  if (state.loading && state.cacheMetrics.totalRequests === 0) {
    return <Loading />;
  }

  return (
    <div className="cache-management-interface">
      <div className="cache-management-header">
        <div>
          <Heading size="lg">Cache Management</Heading>
          <p className="cache-management-description">Monitor cache performance and manage invalidation</p>
        </div>

        <div className="cache-management-actions">
          <Button kind="secondary" renderIcon={Analytics} onClick={loadCacheMetrics}>
            Refresh Metrics
          </Button>
          <Button kind="primary" renderIcon={TrashCan} onClick={() => setInvalidateModalOpen(true)}>
            Invalidate Cache
          </Button>
        </div>
      </div>

      {state.error && (
        <div role="alert" aria-live="assertive" className="cache-error-notification">
          <InlineNotification
            kind="error"
            title="Cache Management Error"
            subtitle={state.error}
            onCloseButtonClick={() => setState(prev => ({ ...prev, error: null }))}
          />
        </div>
      )}

      {/* Cache Metrics Overview */}
      <section aria-label="Cache performance metrics" className="cache-metrics-section">
        <Grid className="cache-metrics-grid">
          <Column lg={3} md={2} sm={1}>
            {renderMetricsTile(
              'Hit Rate',
              `${state.cacheMetrics.hitRate.toFixed(1)}%`,
              'Cache efficiency',
              state.cacheMetrics.hitRate < 70 ? 'warning' : state.cacheMetrics.hitRate < 50 ? 'error' : 'good'
            )}
          </Column>
          <Column lg={3} md={2} sm={1}>
            {renderMetricsTile('Total Requests', state.cacheMetrics.totalRequests.toLocaleString(), 'Last 24 hours')}
          </Column>
          <Column lg={3} md={2} sm={1}>
            {renderMetricsTile(
              'Avg Response',
              `${state.cacheMetrics.avgResponseTime}ms`,
              'Response time',
              state.cacheMetrics.avgResponseTime > 500
                ? 'warning'
                : state.cacheMetrics.avgResponseTime > 1000
                  ? 'error'
                  : 'good'
            )}
          </Column>
          <Column lg={3} md={2} sm={1}>
            {renderMetricsTile(
              'Cache Size',
              `${(state.cacheMetrics.cacheSize / 1024 / 1024).toFixed(1)} MB`,
              'Storage used'
            )}
          </Column>
        </Grid>
      </section>

      {/* Performance Alerts */}
      {state.cacheMetrics.hitRate < 70 && (
        <div role="alert" aria-live="polite" className="cache-alert-section">
          <InlineNotification
            kind="warning"
            title="Low Cache Hit Rate"
            subtitle={`Current hit rate is ${state.cacheMetrics.hitRate.toFixed(1)}%. Consider reviewing cache strategies.`}
          />
        </div>
      )}

      {/* Cache Invalidation Progress */}
      <section aria-label="Cache invalidation progress" className="cache-progress-section">
        <Heading size="md" className="cache-section-heading">
          Cache Invalidation Progress
        </Heading>
        <ProgressIndicator
          currentIndex={
            state.invalidationHistory.length > 0
              ? Math.floor(
                  (state.invalidationHistory.filter(h => h.status === 'completed').length /
                    Math.max(state.invalidationHistory.length, 1)) *
                    4
                )
              : 0
          }
          spaceEqually
        >
          <ProgressStep label="Queued" description="Invalidation requests queued" />
          <ProgressStep label="Processing" description="Cache entries being invalidated" />
          <ProgressStep label="Propagating" description="Changes propagating globally" />
          <ProgressStep label="Complete" description="Cache invalidation completed" />
        </ProgressIndicator>
      </section>

      {/* Invalidation History */}
      <section aria-label="Cache invalidation history" className="cache-history-section">
        <Heading size="md" className="cache-section-heading">
          Recent Cache Invalidations
        </Heading>
        <DataTable
          rows={invalidationRows}
          headers={invalidationHeaders}
          render={({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => (
            <TableContainer>
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader key={header.key} {...getHeaderProps({ header })}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length} className="cache-empty-state">
                        <p className="cache-empty-text">No recent invalidations</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map(row => (
                      <TableRow key={row.id} {...getRowProps({ row })}>
                        {row.cells.map(cell => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        />
      </section>

      {/* Cache Invalidation Modal */}
      <Modal
        open={invalidateModalOpen}
        onRequestClose={() => setInvalidateModalOpen(false)}
        modalHeading="Invalidate Cache"
        modalLabel="Cache Management"
        primaryButtonText="Invalidate"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleBulkInvalidate}
      >
        <div className="cache-modal-form">
          <TextInput
            id="content-ids"
            labelText="Content IDs"
            placeholder="Enter content IDs separated by commas"
            helperText="Leave empty to invalidate all cache entries"
            value={contentIdsToInvalidate}
            onChange={e => setContentIdsToInvalidate(e.target.value)}
          />
        </div>

        <InlineNotification
          kind="warning"
          title="Cache Invalidation Impact"
          subtitle="Invalidating cache will temporarily reduce performance until cache is rebuilt."
          lowContrast
        />
      </Modal>
    </div>
  );
};

export default CacheManagementInterface;
